import React, { ChangeEvent, MouseEventHandler, Ref } from 'react'
import TextareaAutoSize from 'react-textarea-autosize'

import clsx from 'clsx'

import { FieldType } from '@/components/view/atoms/textfield/TextField'

const AREA_STRING_MAX_LENGTH = 5000

interface BaseProps {
  id?: string
  name?: string
  /** 制御コンポーネントとして使うときの値。**渡さなければ非制御のまま**で、
   * react-hook-form 経由（FormTextArea）の既存利用はこちら。 */
  value?: string
  /** 文字数カウンタの表示だけに使う値。非制御で使いつつ件数を出したいとき用。
   * `value` を兼ねさせると、RHF 経由の全画面が制御コンポーネントに変わってしまう */
  counterValue?: string
  onChange(event: ChangeEvent<HTMLTextAreaElement>): void
  onBlur?(event: ChangeEvent<HTMLTextAreaElement>): void
  inputRef?: Ref<HTMLTextAreaElement>
  required?: boolean
  minRows?: number
  maxLength?: number
  minLength?: number
  placeholder?: string
  autoComplete?: string
  autoFocus?: boolean
  error?: boolean
  disabled?: boolean
  memo?: boolean
  counter?: boolean
  buttonOption?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
  flexGrow?: boolean
  rightBottomNode?: React.ReactNode
  leftBottomNode?: React.ReactNode
  fieldType?: FieldType
  shadow?: boolean
}

interface ButtonOptionProps extends BaseProps {
  buttonOption: true
  onClick: MouseEventHandler<HTMLButtonElement>
}

interface NoButtonOptionProps extends BaseProps {
  buttonOption?: false
  onClick?: MouseEventHandler<HTMLButtonElement>
}

type Props = ButtonOptionProps | NoButtonOptionProps

const TextArea = ({
  id,
  name = '',
  value,
  counterValue,
  onChange,
  onBlur,
  inputRef,
  maxLength = AREA_STRING_MAX_LENGTH,
  minLength,
  required = false,
  placeholder = '',
  autoComplete = 'off',
  autoFocus = false,
  error = false,
  disabled = false,
  memo = false,
  counter = false,
  minRows = 5,
  flexGrow = false,
  rightBottomNode,
  leftBottomNode,
  fieldType = FieldType.DEFAULT,
  shadow = false
}: Props) => {
  const hoverActiveClass = memo
    ? 'hover:bg-input-emphasis-hover active:bg-input-emphasis-active'
    : 'hover:bg-surface-hover hover:border-input-hover active:bg-surface-active active:border-input-active'

  const bgClass = error
    ? 'bg-surface-critical'
    : disabled
      ? 'bg-surface-disabled pointer-events-none'
      : memo
        ? 'bg-input-emphasis'
        : 'bg-surface'

  const placeholderShownClass =
    (fieldType == FieldType.REQUIRED || fieldType == FieldType.SEMI_REQUIRED) &&
    'peer-placeholder-shown:border-critical-secondary border-critical'

  // value は渡されたときだけ textarea へ適用する。常に当てると、react-hook-form 経由で
  // 非制御に使っている既存画面が制御コンポーネントに変わってしまう
  const controlledValue = value !== undefined ? { value } : {}

  const Tag = flexGrow ? 'textarea' : TextareaAutoSize
  return (
    <>
      <div
        id={id}
        className={clsx(
          bgClass,
          hoverActiveClass,
          'text-size-13 relative w-full rounded-lg leading-150',
          flexGrow && 'flex grow flex-col'
        )}
      >
        <Tag
          className={clsx(
            'bg-surface-transparent text-primary placeholder:text-placeholder peer relative z-20 block w-full px-2.5 py-1.5 focus:appearance-none focus:outline-none',
            flexGrow && 'grow',
            shadow && 'shadow-scroll'
          )}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          ref={inputRef}
          required={required}
          maxLength={maxLength}
          minLength={minLength}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          disabled={disabled}
          {...controlledValue}
          {...(!flexGrow && { minRows })}
        />
        <span
          className={clsx(
            'pointer-events outline-default-peer absolute inset-0 shrink rounded-lg border',
            error ? 'border-critical-secondary' : disabled ? 'border-none' : 'border-border-input',
            placeholderShownClass,
            fieldType == FieldType.REQUIRED && 'peer-placeholder-shown:bg-surface-critical'
          )}
        />
        {counter && (
          <span className='text-secondary block pr-2.5 pb-1 text-right'>
            {(counterValue ?? value)?.length ?? 0}/{maxLength}
          </span>
        )}
        {(rightBottomNode || leftBottomNode) && (
          <div
            className={clsx(
              'flex items-center',
              rightBottomNode && leftBottomNode
                ? 'justify-between'
                : leftBottomNode
                  ? 'justify-start'
                  : 'justify-end',
              'relative px-3 py-2'
            )}
          >
            {leftBottomNode && <>{leftBottomNode}</>}
            {rightBottomNode && <>{rightBottomNode}</>}
          </div>
        )}
      </div>
    </>
  )
}

export default TextArea
